
import { X, CheckCircle2, Archive, RotateCcw } from "lucide-react";

interface BatchPromoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  actionLoading: boolean;
  rulesToActivateCount: number;
  rulesToSupersedeCount: number;
  knowledgeToPublishCount: number;
  guardrailsToActivateCount?: number;
}

export function BatchPromoteModal({
  isOpen,
  onClose,
  onConfirm,
  actionLoading,
  rulesToActivateCount,
  rulesToSupersedeCount,
  knowledgeToPublishCount,
  guardrailsToActivateCount = 0,
}: BatchPromoteModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-stone-900/60 backdrop-blur-xs p-4">
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 shadow-2xl max-w-md w-full p-5 space-y-4">
        <div className="flex items-center gap-2.5 text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="w-5 h-5" />
          <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">
            Confirm Changeset Promotion
          </h3>
        </div>

        <p className="text-xs text-stone-600 dark:text-stone-400 leading-relaxed">
          Promoting this batch will atomically activate candidate draft rules and publish knowledge items. Prior superseded rules will be archived.
        </p>

        <div className="p-3 rounded-xl bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 text-xs space-y-1.5">
          <div className="flex justify-between">
            <span>Rules to Activate:</span>
            <strong className="text-emerald-600 font-mono">
              {rulesToActivateCount}
            </strong>
          </div>
          <div className="flex justify-between">
            <span>Rules to Supersede:</span>
            <strong className="text-amber-600 font-mono">
              {rulesToSupersedeCount}
            </strong>
          </div>
          <div className="flex justify-between">
            <span>Knowledge Items to Publish:</span>
            <strong className="text-emerald-600 font-mono">
              {knowledgeToPublishCount}
            </strong>
          </div>
          {guardrailsToActivateCount > 0 && (
            <div className="flex justify-between">
              <span>Guardrail Policies to Activate:</span>
              <strong className="text-emerald-600 font-mono">
                {guardrailsToActivateCount}
              </strong>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-stone-100 dark:border-stone-800">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-xs text-stone-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={actionLoading}
            className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs disabled:opacity-50 transition"
          >
            {actionLoading ? "Promoting..." : "Confirm & Promote to Production"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface BatchRejectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  actionLoading: boolean;
}

export function BatchRejectModal({
  isOpen,
  onClose,
  onConfirm,
  actionLoading,
}: BatchRejectModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-stone-900/60 backdrop-blur-xs p-4">
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 shadow-2xl max-w-md w-full p-5 space-y-4">
        <div className="flex items-center gap-2.5 text-rose-600">
          <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">
            Reject Feedback Batch
          </h3>
        </div>

        <p className="text-xs text-stone-600 dark:text-stone-400 leading-relaxed">
          Rejecting will mark all candidate draft rules and knowledge items in this batch as discarded, and transition the batch to archived.
        </p>

        <div className="flex justify-end gap-2 pt-2 border-t border-stone-100 dark:border-stone-800">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-xs text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={actionLoading}
            className="px-4 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs disabled:opacity-50 transition"
          >
            {actionLoading ? "Rejecting..." : "Confirm Rejection"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface BatchArchiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  actionLoading: boolean;
  batchTitle?: string;
}

export function BatchArchiveModal({
  isOpen,
  onClose,
  onConfirm,
  actionLoading,
  batchTitle,
}: BatchArchiveModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-stone-900/60 backdrop-blur-xs p-4">
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 shadow-2xl max-w-md w-full p-5 space-y-4">
        <div className="flex items-center gap-2.5 text-stone-800 dark:text-stone-200">
          <div className="w-8 h-8 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center text-stone-600 dark:text-stone-400">
            <Archive className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">
              Archive Feedback Batch
            </h3>
            {batchTitle && (
              <p className="text-[11px] text-stone-400 font-mono truncate max-w-xs">{batchTitle}</p>
            )}
          </div>
        </div>

        <p className="text-xs text-stone-600 dark:text-stone-400 leading-relaxed">
          Are you sure you want to archive this feedback batch? It will be moved to the Archived tab. Any pending candidate draft proposals will be discarded.
        </p>

        <div className="flex justify-end gap-2 pt-2 border-t border-stone-100 dark:border-stone-800">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-xs text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={actionLoading}
            className="px-4 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 hover:bg-stone-800 dark:hover:bg-white text-white dark:text-stone-900 font-bold text-xs disabled:opacity-50 transition"
          >
            {actionLoading ? "Archiving..." : "Archive Batch"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface BatchReopenModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  actionLoading: boolean;
  batchTitle?: string;
  draftRulesCount: number;
  draftKnowledgeCount: number;
  draftGuardrailsCount?: number;
}

export function BatchReopenModal({
  isOpen,
  onClose,
  onConfirm,
  actionLoading,
  batchTitle,
  draftRulesCount,
  draftKnowledgeCount,
  draftGuardrailsCount = 0,
}: BatchReopenModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-stone-900/60 backdrop-blur-xs p-4">
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-amber-200 dark:border-amber-800 shadow-2xl max-w-md w-full p-5 space-y-4">
        <div className="flex items-center gap-2.5 text-amber-600 dark:text-amber-500">
          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-950/60 flex items-center justify-center text-amber-700 dark:text-amber-400">
            <RotateCcw className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">
              Return Batch to Collecting Mode
            </h3>
            {batchTitle && (
              <p className="text-[11px] text-stone-400 font-mono truncate max-w-xs">{batchTitle}</p>
            )}
          </div>
        </div>

        <div className="space-y-2 text-xs text-stone-600 dark:text-stone-400 leading-relaxed">
          <p>
            Returning this batch to collecting mode will <strong>permanently delete all proposed draft changesets</strong> ({draftRulesCount} rules, {draftKnowledgeCount} knowledge items{draftGuardrailsCount > 0 ? `, ${draftGuardrailsCount} guardrails` : ""}) and reset member comments to open.
          </p>
          <p className="text-amber-700 dark:text-amber-400/90 font-medium">
            New processing will be required to synthesize candidate rules again once you are ready.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-stone-100 dark:border-stone-800">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-xs text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={actionLoading}
            className="px-4 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs disabled:opacity-50 transition shadow-2xs"
          >
            {actionLoading ? "Resetting..." : "Yes, Reset to Collecting"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface BatchEditRuleModalProps {
  rule: any;
  onClose: () => void;
  onSave: (e: React.FormEvent) => void;
  title: string;
  onChangeTitle: (val: string) => void;
  instruction: string;
  onChangeInstruction: (val: string) => void;
  priority: number;
  onChangePriority: (val: number) => void;
  saving: boolean;
  baselineRule?: any;
}

export function BatchEditRuleModal({
  rule,
  onClose,
  onSave,
  title,
  onChangeTitle,
  instruction,
  onChangeInstruction,
  priority,
  onChangePriority,
  saving,
  baselineRule,
}: BatchEditRuleModalProps) {
  if (!rule) return null;

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-stone-900/60 backdrop-blur-xs p-4">
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 shadow-2xl max-w-lg w-full p-5 space-y-4">
        <div className="flex items-center justify-between border-b border-stone-100 dark:border-stone-800 pb-3">
          <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">Edit Proposed Rule Draft</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave(e); }} className="space-y-3 text-xs">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block font-semibold text-stone-700 dark:text-stone-300">
                Rule Title
              </label>
              {baselineRule && baselineRule.title !== title && (
                <span className="text-[11px] text-stone-400 font-mono">
                  Baseline: {baselineRule.title}
                </span>
              )}
            </div>
            <input
              type="text"
              value={title}
              onChange={(e) => onChangeTitle(e.target.value)}
              className="w-full rounded-lg border border-stone-200 dark:border-stone-800 p-2 text-xs text-stone-900 dark:text-stone-100"
            />
          </div>

          <div>
            <label className="block font-semibold text-stone-700 dark:text-stone-300 mb-1">
              Instruction
            </label>
            <textarea
              rows={5}
              value={instruction}
              onChange={(e) => onChangeInstruction(e.target.value)}
              className="w-full rounded-lg border border-stone-200 dark:border-stone-800 p-2 text-xs text-stone-900 dark:text-stone-100 font-mono"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block font-semibold text-stone-700 dark:text-stone-300">
                Priority
              </label>
              {baselineRule && (
                <span className="text-[11px] text-stone-400 font-mono">
                  Baseline priority: <strong className="text-stone-600 dark:text-stone-300">{baselineRule.priority}</strong>
                  {baselineRule.priority !== priority && (
                    <span className="text-amber-600 dark:text-amber-400 ml-1">(changed to {priority})</span>
                  )}
                </span>
              )}
            </div>
            <input
              type="number"
              value={priority}
              onChange={(e) => onChangePriority(parseInt(e.target.value) || 100)}
              className="w-32 rounded-lg border border-stone-200 dark:border-stone-800 p-2 text-xs text-stone-900 dark:text-stone-100 font-mono"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-stone-100 dark:border-stone-800">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-stone-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Draft"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface BatchEditKnowledgeModalProps {
  item: any;
  onClose: () => void;
  onSave: (e: React.FormEvent) => void;
  title: string;
  onChangeTitle: (val: string) => void;
  content: string;
  onChangeContent: (val: string) => void;
  saving: boolean;
}

export function BatchEditKnowledgeModal({
  item,
  onClose,
  onSave,
  title,
  onChangeTitle,
  content,
  onChangeContent,
  saving,
}: BatchEditKnowledgeModalProps) {
  if (!item) return null;

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-stone-900/60 backdrop-blur-xs p-4">
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 shadow-2xl max-w-lg w-full p-5 space-y-4">
        <div className="flex items-center justify-between border-b border-stone-100 dark:border-stone-800 pb-3">
          <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">Edit Proposed Knowledge Item</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave(e); }} className="space-y-3 text-xs">
          <div>
            <label className="block font-semibold text-stone-700 dark:text-stone-300 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => onChangeTitle(e.target.value)}
              className="w-full rounded-lg border border-stone-200 dark:border-stone-800 p-2 text-xs text-stone-900 dark:text-stone-100"
            />
          </div>

          <div>
            <label className="block font-semibold text-stone-700 dark:text-stone-300 mb-1">
              Content (Markdown)
            </label>
            <textarea
              rows={8}
              value={content}
              onChange={(e) => onChangeContent(e.target.value)}
              className="w-full rounded-lg border border-stone-200 dark:border-stone-800 p-2 text-xs text-stone-900 dark:text-stone-100 font-mono"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-stone-100 dark:border-stone-800">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-stone-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Draft"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
